import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, FileSignature } from "lucide-react";

const STATUSES = [
  { v: "active", l: "Active", cls: "bg-emerald-100 text-emerald-700" },
  { v: "pending", l: "Pending", cls: "bg-amber-100 text-amber-800" },
  { v: "expired", l: "Expired", cls: "bg-red-100 text-red-700" },
  { v: "none", l: "No contract", cls: "bg-slate-100 text-slate-600" },
];
const CYCLES = ["monthly", "quarterly", "annual"];

const F = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

export default function ContractTab({ accountId }) {
  const [c, setC] = useState({ status: "none", start_date: "", renewal_date: "", term_months: "", billing_cycle: "monthly", notes: "" });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchContract = () => {
    setLoading(true);
    setLoadError(false);
    api.get(`/admin/accounts/${accountId}/contract`)
      .then(({ data }) => setC((prev) => ({ ...prev, ...(data.contract || {}) })))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchContract(); /* eslint-disable-next-line */ }, [accountId]);

  const set = (k) => (e) => setC((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...c, term_months: c.term_months === "" ? null : Number(c.term_months) };
      await api.put(`/admin/accounts/${accountId}/contract`, payload);
      toast.success("Contract saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading contract…</div>;
  if (loadError) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center" data-testid="contract-error">
      <p className="text-sm font-semibold text-red-700">Couldn't load the contract.</p>
      <button onClick={fetchContract} className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700">Retry</button>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid="contract-tab">
      <div className="flex items-center gap-2">
        <FileSignature className="w-5 h-5 text-slate-500" />
        <h3 className="text-base font-bold text-slate-900">Contract & terms</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <F label="Status">
          <select data-testid="contract-status" value={c.status || "none"} onChange={set("status")} className={inputCls + " bg-white"}>
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
        </F>
        <F label="Billing cycle">
          <select data-testid="contract-cycle" value={c.billing_cycle || "monthly"} onChange={set("billing_cycle")} className={inputCls + " bg-white capitalize"}>
            {CYCLES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </F>
        <F label="Start date"><input data-testid="contract-start" type="date" value={c.start_date || ""} onChange={set("start_date")} className={inputCls} /></F>
        <F label="Renewal date"><input data-testid="contract-renewal" type="date" value={c.renewal_date || ""} onChange={set("renewal_date")} className={inputCls} /></F>
        <F label="Term (months)"><input data-testid="contract-term" type="number" min="0" value={c.term_months ?? ""} onChange={set("term_months")} placeholder="12" className={inputCls} /></F>
      </div>
      <F label="Notes">
        <textarea data-testid="contract-notes" rows={3} value={c.notes || ""} onChange={set("notes")} placeholder="SLA terms, special pricing arrangements, renewal reminders…" className={inputCls} />
      </F>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="contract-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save contract
        </button>
      </div>
    </div>
  );
}
